import Footer from "components/checkout/CheckoutFooter";
import Head from "next/head";
import MorganStyles from "globalStyles/morgan";
import { cloneElement, useEffect } from "react";
import Messages from "components/Layout/Messages";
import { withRouter } from "next/router";
import PropTypes from "prop-types";
import cx from "classnames";
import styled, { keyframes } from "styled-components";

export const FlowType = {
  GUIDED_SIGN_UP_FUNNEL: "guidedSignUpFunnel",
  SUBSCRIBE: "subscribe",
  GIVE_SUBSCRIPTION: "give-subscription",
  PROFILE: "profile",
};

const GIVE_SUBSCRIPTION_STEPS = [
  { slug: "personalize", label: "Personalize" },
  { slug: "choose-gift", label: "Choose" },
  { slug: "gift-details", label: "Details" },
  { slug: "checkout", label: "Checkout" },
].map((s) => ({ ...s, route: `/give-subscription/${s.slug}` }));

const PROFILE_STEPS = [
  { slug: "", label: "My Profile" },
  { slug: "add-payment", label: "Payment Method" },
].map((s) => ({ ...s, route: `/profile/${s.slug}` }));

const SUBSCRIBE_STEPS = [
  { slug: "personalize", label: "Personalize" },
  { slug: "checkout", label: "Checkout" },
].map((s) => ({ ...s, route: `/subscribe/${s.slug}` }));

const GUIDED_SIGN_UP_FUNNEL = [
  { slug: "preferences", label: "Preferences" },
].map((s) => ({ ...s, route: `/subscribe/${s.slug}` }));

const getSteps = (flow) =>
  ({
    [FlowType.GUIDED_SIGN_UP_FUNNEL]: GUIDED_SIGN_UP_FUNNEL,
    [FlowType.SUBSCRIBE]: SUBSCRIBE_STEPS,
    [FlowType.GIVE_SUBSCRIPTION]: GIVE_SUBSCRIPTION_STEPS,
    [FlowType.PROFILE]: PROFILE_STEPS,
  }[flow] || []);

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const StyledLayout = styled.div`
  main {
    animation: ${fadeIn} 0.2s ease forwards;
  }
`;

const getActiveSlug = (route) =>
  route?.split("/").slice(-1)[0].replace("/", "");

/**
 * Provides layout and state connection for checkout page.
 *
 * @param {Object} props
 * @param {Object} props.children
 * @param {Object} props.flow
 * @param {Object} props.router
 */
export const CheckoutContainer = ({ children, flow, router }) => {
  const steps = getSteps(flow);

  const activeSlug = getActiveSlug(router.route);
  const activeStep = steps?.find((s) => s.slug === activeSlug);

  const nextStepIndex = steps?.indexOf(activeStep) + 1;
  const nextStep =
    nextStepIndex < steps?.length ? steps[nextStepIndex] : steps?.slice(-1);

  const nextRoute = nextStep?.route;

  useEffect(() => {
    nextRoute && router.prefetch(nextRoute);
  }, [nextRoute, router]);

  const routeNext = () => router?.push(nextRoute);

  return (
    <StyledLayout
      className="layout checkout morgan"
      data-analytics="sub-checkout"
    >
      <Head>
        <meta name="robots" content="noindex" />
      </Head>
      <Messages />
      <main className={cx("main pb-16", flow)} key={router?.pathname}>
        {cloneElement(children, { routeNext })}
      </main>
      <Footer />
      <MorganStyles />
    </StyledLayout>
  );
};

CheckoutContainer.propTypes = {
  flow: PropTypes.string,
  router: PropTypes.object,
  children: PropTypes.element.isRequired,
  noNav: PropTypes.bool,
};

const ContainerWithRouter = withRouter(CheckoutContainer);

/**
 *   getLayout exports are used for persistence, preventing a full rerender of the container
 */

export const getGiftLayout = (page) => (
  <ContainerWithRouter flow={"give-subscription"}>{page}</ContainerWithRouter>
);

export const getProfileLayout = (page) => (
  <ContainerWithRouter flow={"profile"}>{page}</ContainerWithRouter>
);

export const getSubscribeLayout = (page) => (
  <ContainerWithRouter flow={"subscribe"}>{page}</ContainerWithRouter>
);

export default ContainerWithRouter;
